import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Award, Gift, UserPlus, Clock, CheckCircle2, Settings2 } from "lucide-react";

export default function ProLoyalty() {
  const [settings, setSettings] = useState({ loyalty_threshold: 10, loyalty_reward: "" });
  const [cards, setCards] = useState([]);
  const [comeback, setComeback] = useState([]);
  const [threshold, setThreshold] = useState(10);
  const [reward, setReward] = useState("");
  const [credit, setCredit] = useState({ customer_key: "", customer_name: "", points: 1 });
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get("/pro/loyalty/settings").then((r) => { setSettings(r.data); setThreshold(r.data.loyalty_threshold); setReward(r.data.loyalty_reward || ""); }).catch(() => {});
    api.get("/pro/loyalty/cards").then((r) => setCards(r.data.items || [])).catch(() => {});
    api.get("/pro/loyalty/comeback?days=30").then((r) => setComeback(r.data.items || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try { await api.patch("/pro/loyalty/settings", { loyalty_threshold: Number(threshold), loyalty_reward: reward }); toast.success("Programme mis à jour"); loadAll(); }
    catch { toast.error("Erreur"); }
  };

  const doCredit = async (e) => {
    e.preventDefault();
    if (!credit.customer_key.trim()) { toast.error("Indiquez le téléphone ou l'email du client."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pro/loyalty/credit", { ...credit, points: Number(credit.points) || 1 });
      if (data.reward_ready) toast.success(`🎁 Récompense atteinte ! ${data.points} pts (${data.reward || "récompense"})`, { duration: 6000 });
      else toast.success(`+${credit.points} pt(s) · ${data.points}/${data.threshold}`);
      setCredit({ customer_key: "", customer_name: "", points: 1 });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const redeem = async (key) => {
    if (!window.confirm("Échanger une récompense pour ce client ? Les points du palier seront déduits.")) return;
    try { await api.post("/pro/loyalty/redeem", { customer_key: key }); toast.success("Récompense échangée ✓"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const th = settings.loyalty_threshold || 10;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="pro-loyalty-page">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Award size={22} className="text-[#E05638]" /> Carte de fidélité</h1>
        <p className="text-sm text-slate-600 mt-1">Attribuez des points à vos clients à chaque passage. Au palier atteint, offrez la récompense pour les faire revenir.</p>
      </header>

      {/* Attribuer des points */}
      <form onSubmit={doCredit} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="loyalty-credit-form">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><UserPlus size={18} className="text-[#E05638]" /> Attribuer des points en caisse</div>
        <div className="mt-3 grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2"><Label>Client (téléphone ou email)</Label><Input value={credit.customer_key} onChange={(e) => setCredit((c) => ({ ...c, customer_key: e.target.value }))} placeholder="06 12 34 56 78" data-testid="loyalty-customer-key" /></div>
          <div><Label>Nom (facultatif)</Label><Input value={credit.customer_name} onChange={(e) => setCredit((c) => ({ ...c, customer_name: e.target.value }))} data-testid="loyalty-customer-name" /></div>
          <div><Label>Points</Label><Input type="number" min={1} value={credit.points} onChange={(e) => setCredit((c) => ({ ...c, points: e.target.value }))} data-testid="loyalty-points" /></div>
        </div>
        <Button type="submit" disabled={busy} className="mt-3 btn-shine text-white" data-testid="loyalty-credit-submit">{busy ? "…" : "Ajouter les points"}</Button>
      </form>

      {/* Paramètres du programme */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="loyalty-settings">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Settings2 size={18} className="text-slate-500" /> Mon programme</div>
        <div className="mt-3 grid sm:grid-cols-3 gap-3 items-end">
          <div><Label>Points pour 1 récompense</Label><Input type="number" min={1} value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="loyalty-threshold" /></div>
          <div className="sm:col-span-2"><Label>Récompense offerte</Label><Input value={reward} onChange={(e) => setReward(e.target.value)} placeholder="Ex : 1 café offert, -10% sur l'addition…" data-testid="loyalty-reward" /></div>
        </div>
        <Button variant="outline" onClick={saveSettings} className="mt-3" data-testid="loyalty-settings-save">Enregistrer le programme</Button>
      </div>

      {/* Clients à faire revenir */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="loyalty-comeback">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Clock size={18} className="text-amber-600" /> Clients à faire revenir <span className="text-sm font-normal text-slate-500">(sans visite depuis +30 j)</span></div>
        {comeback.length === 0 ? (
          <div className="mt-2 text-sm text-slate-500">Aucun client inactif pour le moment. 👍</div>
        ) : (
          <ul className="mt-3 divide-y divide-amber-100">
            {comeback.map((c) => (
              <li key={c.id} className="py-2 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-800">{c.customer_name || c.customer_key}</span>
                <span className="text-xs text-slate-500">{c.points} pts · dernière visite {c.last_visit ? new Date(c.last_visit).toLocaleDateString("fr-FR") : "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Toutes les cartes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Gift size={18} className="text-[#E05638]" /> Mes clients fidèles</div>
        {cards.length === 0 ? (
          <div className="mt-2 text-sm text-slate-500">Aucune carte encore. Attribuez des points à votre premier client ci-dessus.</div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="loyalty-cards">
            {cards.map((c) => {
              const ready = (c.points || 0) >= th;
              return (
                <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{c.customer_name || c.customer_key}</div>
                    <div className="text-xs text-slate-500">{c.visits || 0} visite(s){c.rewards_earned ? ` · ${c.rewards_earned} récompense(s) utilisée(s)` : ""}</div>
                    <div className="mt-1.5 h-2 w-40 max-w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${ready ? "bg-emerald-500" : "bg-[#E05638]"}`} style={{ width: `${Math.min(100, Math.round(((c.points || 0) / th) * 100))}%` }} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading font-extrabold text-slate-900">{c.points || 0}<span className="text-xs text-slate-400">/{th}</span></div>
                    {ready && <Button size="sm" onClick={() => redeem(c.customer_key)} className="mt-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`loyalty-redeem-${c.id}`}><CheckCircle2 size={14} className="mr-1" /> Récompense</Button>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
